import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Drawer, Form, Input, Modal, Select, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, TeamOutlined } from '@ant-design/icons';
import {
  createRole,
  listRoleHolders,
  listRoles,
  retireRole,
  updateRole,
  type CustomRole,
} from '@/api/teamAccess';
import { fetchServerMatrix } from '@/api/uam';
import type { UamUserView } from '@/api/types';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { useAuth } from '@/auth/OlAuthContext';
import { formatAbsolute } from '@/lib/time';
import { resolvePermissionMatrix } from './permissionMatrix';
import { bindableKeys, SCOPE_RESOURCE_TYPES } from './bindableKeys';

const { Paragraph, Text } = Typography;

interface RoleForm {
  roleKey: string;
  displayName: string;
  permissionKeys: string[];
  scopeResourceType?: string | null;
}

/**
 * B1 · UC2 §5 — custom tenant-authored roles (Team & Access). The console HIDES what the caller
 * cannot do; the server RE-CHECKS regardless (§12 tamper contract: an unhidden affordance still
 * 403s). The key picker is ALLOWLIST-DRIVEN (create and edit alike): it offers exactly the keys
 * the §5.4 guards would accept — matrix keys the caller's roles hold, minus the recovery plane —
 * so the picker cannot even express ROLE_KEY_UNKNOWN / NOT_BINDABLE / EXCEEDS_AUTHOR; the server
 * still refuses them if the console is tampered with. Edit is a full replacement (PUT — keys +
 * scope clause); the scope clause compiles server-side to exactly one UC1 CONFINE row (§5.5).
 */
export function RolesPanel() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const canWrite = user?.roles?.includes('hub_admin') ?? false;
  const [roles, setRoles] = useState<CustomRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  /** Non-null while the modal edits an existing ACTIVE role (PUT) rather than creating (POST). */
  const [editing, setEditing] = useState<CustomRole | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<RoleForm>();

  // The allowlist the picker offers — the same-commit matrix (server-first, bundle fallback)
  // filtered to the CALLER's own effective keys minus the recovery plane (§5.4 mirrored).
  const [offeredKeys, setOfferedKeys] = useState<string[] | null>(null);

  const [holdersRole, setHoldersRole] = useState<CustomRole | null>(null);
  const [holders, setHolders] = useState<UamUserView[] | null>(null);
  const [holdersError, setHoldersError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      setRoles(await listRoles());
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { model } = await resolvePermissionMatrix(fetchServerMatrix);
        if (alive) setOfferedKeys(bindableKeys(model, user?.roles ?? []));
      } catch {
        if (alive) setOfferedKeys(null); // bundle parse failure — picker degrades to free entry
      }
    })();
    return () => {
      alive = false;
    };
  }, [user?.roles]);

  const keyOptions = useMemo(
    () => (offeredKeys ?? []).map((k) => ({ value: k })),
    [offeredKeys],
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (role: CustomRole) => {
    setEditing(role);
    form.setFieldsValue({
      roleKey: role.roleKey,
      displayName: role.displayName,
      permissionKeys: role.permissionKeys,
      scopeResourceType: role.scopeResourceType ?? undefined,
    });
    setModalOpen(true);
  };

  const submit = async (values: RoleForm) => {
    setSubmitting(true);
    try {
      if (editing) {
        await updateRole(editing.roleId, {
          displayName: values.displayName.trim(),
          permissionKeys: values.permissionKeys,
          scopeResourceType: values.scopeResourceType ?? null,
        });
        message.success(`${editing.roleKey} updated — holders resolve the new keys on their next request`);
      } else {
        await createRole({
          roleKey: values.roleKey.trim(),
          displayName: values.displayName.trim(),
          permissionKeys: values.permissionKeys,
          scopeResourceType: values.scopeResourceType ?? null,
        });
        message.success(`Custom role ${values.roleKey.trim()} created`);
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      void load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  };

  const retire = async (role: CustomRole) => {
    try {
      await retireRole(role.roleId);
      message.success(`${role.roleKey} retired — holders resolve zero keys on their next request`);
      void load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    }
  };

  const openHolders = async (role: CustomRole) => {
    setHoldersRole(role);
    setHolders(null);
    setHoldersError(null);
    try {
      const view = await listRoleHolders(role.roleId);
      setHolders(view.holders);
    } catch (e) {
      // 503 UAM_DIRECTORY_UNAVAILABLE (identity seam absent) et al — say it, never a blank list.
      setHoldersError(normalizeApiError(e).message);
    }
  };

  const columns: ColumnsType<CustomRole> = [
    { title: 'Role', dataIndex: 'roleKey', render: (v: string) => <Text code>{v}</Text> },
    { title: 'Name', dataIndex: 'displayName' },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => (
        <Tag color={s === 'ACTIVE' ? 'green' : s === 'RETIRED' ? 'default' : 'blue'}>{s}</Tag>
      ),
    },
    {
      title: 'Bound keys',
      dataIndex: 'permissionKeys',
      render: (keys: string[]) => (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
          {keys.map((k) => (
            <Tag key={k} style={{ margin: 0 }}>
              {k}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: 'Scope',
      dataIndex: 'scopeResourceType',
      width: 130,
      render: (t: string | null) =>
        t ? <Tag color="purple">{t} only</Tag> : <Text type="secondary">unconfined</Text>,
    },
    { title: 'Created', dataIndex: 'createdAt', width: 170, render: (v) => formatAbsolute(v) },
    {
      title: '',
      key: 'actions',
      width: canWrite ? 210 : 100,
      render: (_: unknown, role: CustomRole) => (
        <span style={{ display: 'inline-flex', gap: 8 }}>
          <Button size="small" icon={<TeamOutlined />} onClick={() => void openHolders(role)}>
            Holders
          </Button>
          {canWrite && role.status === 'ACTIVE' && (
            <>
              <Button size="small" onClick={() => openEdit(role)}>
                Edit
              </Button>
              <Button size="small" danger onClick={() => void retire(role)}>
                Retire
              </Button>
            </>
          )}
        </span>
      ),
    },
  ];

  if (unavailable) {
    return <EmptyState title="Custom roles are not available" description="The IAM UC2 surface is not enabled in this deployment." />;
  }

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 12, maxWidth: 780, fontSize: 13 }}>
        Tenant-authored roles (<Text code>x_&lt;slug&gt;</Text>) bind existing permission-matrix keys —
        never new ones. A role can never carry authority its author lacks; recovery-plane keys are never
        bindable; retirement bites on the holder&apos;s next request, no re-login. Assign them through
        Users like any catalogue role.
      </Paragraph>
      {canWrite && (
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ marginBottom: 12 }}>
          New custom role
        </Button>
      )}
      <Table rowKey="roleId" size="small" columns={columns} dataSource={roles} loading={loading}
             pagination={{ pageSize: 20, hideOnSinglePage: true }} />

      <Modal title={editing ? `Edit ${editing.roleKey}` : 'New custom role'} open={modalOpen}
             onCancel={() => { setModalOpen(false); setEditing(null); }}
             onOk={() => form.submit()} confirmLoading={submitting}
             okText={editing ? 'Save changes' : 'Create'}>
        <Form form={form} layout="vertical" onFinish={(v) => void submit(v)}>
          <Form.Item name="roleKey" label="Role key" rules={editing ? [] : [
            { required: true, message: 'x_<slug> required' },
            { pattern: /^x_[a-z0-9_]{1,60}$/, message: 'x_ then lowercase a–z, 0–9, _' },
          ]}
            extra={editing ? 'The role key is the stable identity — it never changes.' : undefined}>
            <Input placeholder="x_kyc_reviewer" disabled={editing !== null} />
          </Form.Item>
          <Form.Item name="displayName" label="Display name" rules={[{ required: true }]}>
            <Input placeholder="KYC reviewer" />
          </Form.Item>
          <Form.Item name="permissionKeys" label="Permission keys (existing matrix keys)"
                     rules={[{ required: true, message: 'at least one key' }]}
                     extra={offeredKeys
                       ? 'Offered: matrix keys your own roles hold, minus the recovery plane — the same set the server accepts (it re-checks regardless).'
                       : 'The matrix could not be read — enter keys freely; the server refuses unknown, recovery-plane and beyond-author keys.'}>
            {offeredKeys ? (
              <Select mode="multiple" placeholder="workitems.read, workitems.disposition, …"
                      options={keyOptions} showSearch optionFilterProp="value" />
            ) : (
              <Select mode="tags" placeholder="workitems.read, workitems.disposition, …" tokenSeparators={[',', ' ']} />
            )}
          </Form.Item>
          <Form.Item name="scopeResourceType" label="Scope clause (optional)"
                     extra="Confines the role to ONE resource type — it compiles to a single CONFINE rule on the ACL, subtracting, never adding. Blank = unconfined.">
            <Select allowClear placeholder="unconfined"
                    options={SCOPE_RESOURCE_TYPES.map((t) => ({ value: t }))} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer title={holdersRole ? `Holders — ${holdersRole.roleKey}` : 'Holders'}
              open={holdersRole !== null}
              onClose={() => { setHoldersRole(null); setHolders(null); setHoldersError(null); }}
              width={520}>
        {holdersError ? (
          <Paragraph type="secondary">{holdersError}</Paragraph>
        ) : holders !== null && holders.length === 0 ? (
          <Paragraph type="secondary">
            Nobody holds this role right now. Assign it on the Users tab — it appears there like any
            catalogue role while the role is ACTIVE.
          </Paragraph>
        ) : (
          <Table rowKey="userId" size="small" loading={holders === null} dataSource={holders ?? []}
                 pagination={false}
                 columns={[
                   { title: 'Username', dataIndex: 'username', render: (v: string) => <Text code>{v}</Text> },
                   { title: 'Email', dataIndex: 'email', render: (v: string | null) => v ?? '—' },
                   {
                     title: 'Status', dataIndex: 'enabled', width: 90,
                     render: (enabled: boolean) =>
                       enabled ? <Tag color="green">enabled</Tag> : <Tag>disabled</Tag>,
                   },
                   { title: 'Spoke', dataIndex: 'spokeId', width: 110, render: (v: string | null) => v ?? 'hub' },
                 ]} />
        )}
      </Drawer>
    </div>
  );
}
